import React, { useState, useEffect } from 'react';
import { Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import { useDraggable } from '@dnd-kit/core';
import * as pdfjs from 'pdfjs-dist';
import { LibraryFile, formatFileSize } from 'mock/folder.data';
import { streamFile } from '../function/api';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

interface Props {
  file: LibraryFile;
  variant: 'grid' | 'list';
  isDragOverlay?: boolean;
  onMove?: () => void;
  onRename?: () => void;
  onShare?: () => void;
  onDownload?: () => void;
  onOpen?: () => void;
  onDelete?: () => void;
  isAdmin?: boolean;
}

const TYPE_META: Record<LibraryFile['type'], { label: string; color: string; bg: string }> = {
  PDF:   { label: 'PDF',  color: '#FF4D4F', bg: 'rgba(255,255,255,0.12)' },
  DOCX:  { label: 'DOC',  color: '#1677FF', bg: 'rgba(255,255,255,0.12)' },
  XLSX:  { label: 'XLS',  color: '#52C41A', bg: 'rgba(255,255,255,0.12)' },
  PPTX:  { label: 'PPT',  color: '#FA8C16', bg: 'rgba(255,255,255,0.12)' },
  VIDEO: { label: 'VID',  color: '#722ED1', bg: 'rgba(255,255,255,0.12)' },
  IMAGE: { label: 'IMG',  color: '#13C2C2', bg: 'rgba(255,255,255,0.12)' },
};

const DROPDOWN_MENU_STYLE: React.CSSProperties = { minWidth: 200 };

const buildMenu = (
  file: LibraryFile,
  onMove?: () => void,
  onRename?: () => void,
  onShare?: () => void,
  onDownload?: () => void,
  onOpen?: () => void,
  onDelete?: () => void,
  isAdmin?: boolean,
): MenuProps['items'] => {
  const items: MenuProps['items'] = [];

  if (onOpen) {
    items.push({ key: 'open', label: 'Mở', onClick: (info) => { info.domEvent.stopPropagation(); onOpen(); } });
  }
  if (onShare) {
    items.push({ key: 'share', label: 'Chia sẻ', onClick: (info) => { info.domEvent.stopPropagation(); onShare(); } });
  }
  if (onDownload) {
    items.push({ key: 'download', label: 'Tải xuống', onClick: (info) => { info.domEvent.stopPropagation(); onDownload(); } });
  }

  if (!file.isAdminUpload || isAdmin) {
    if (onRename) {
      items.push({ key: 'rename', label: 'Đổi tên', onClick: (info) => { info.domEvent.stopPropagation(); onRename(); } });
    }
    if (onMove) {
      items.push({ key: 'move', label: 'Di chuyển', onClick: (info) => { info.domEvent.stopPropagation(); onMove(); } });
    }
    if (onDelete) {
      items.push({ type: 'divider' });
      items.push({ key: 'delete', label: 'Xóa', danger: true, onClick: (info) => { info.domEvent.stopPropagation(); onDelete(); } });
    }
  }
  return items;
};

const MoreBtn: React.FC<{ onClick: (e: React.MouseEvent) => void }> = ({ onClick }) => (
  <button className="lib-more-btn" onClick={onClick}>
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <circle cx="12" cy="5" r="1.5" /><circle cx="12" cy="12" r="1.5" /><circle cx="12" cy="19" r="1.5" />
    </svg>
  </button>
);

const FileCard: React.FC<Props> = ({ file, variant, isDragOverlay, onMove, onRename, onShare, onDownload, onOpen, onDelete, isAdmin }) => {
  const { setNodeRef, attributes, listeners, isDragging } = useDraggable({ id: file.id });
  const meta = TYPE_META[file.type];
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [loadingPreview, setLoadingPreview] = useState<boolean>(false);

  useEffect(() => {
    let active = true;
    let objectUrl: string | null = null;

    if ((file.type === 'IMAGE' || file.type === 'PDF') && file.id) {
      setLoadingPreview(true);
      const chunks: Uint8Array[] = [];

      streamFile(
        file.id,
        (chunk) => {
          if (active) chunks.push(chunk);
        },
        async () => {
          if (!active || chunks.length === 0) return;

          if (file.type === 'IMAGE') {
            const blob = new Blob(chunks as BlobPart[], { type: 'image/*' });
            objectUrl = URL.createObjectURL(blob);
            setPreviewUrl(objectUrl);
            setLoadingPreview(false);
          } else if (file.type === 'PDF') {
            try {
              // Join chunks into a single ArrayBuffer / Uint8Array
              const totalLength = chunks.reduce((acc, c) => acc + c.length, 0);
              const merged = new Uint8Array(totalLength);
              let offset = 0;
              for (const c of chunks) {
                merged.set(c, offset);
                offset += c.length;
              }

              const loadingTask = pdfjs.getDocument({ data: merged.buffer });
              const pdf = await loadingTask.promise;
              const page = await pdf.getPage(1);
              const viewport = page.getViewport({ scale: 0.5 });

              const canvas = document.createElement('canvas');
              const context = canvas.getContext('2d');
              canvas.width = viewport.width;
              canvas.height = viewport.height;

              if (context) {
                await (page as any).render({ canvasContext: context, viewport }).promise;
                if (active) {
                  setPreviewUrl(canvas.toDataURL('image/png'));
                }
              }
            } catch (err) {
              console.error('Error rendering PDF first page preview:', err);
            } finally {
              if (active) setLoadingPreview(false);
            }
          }
        },
        (err) => {
          console.error('Error loading preview via streamFile:', err);
          if (active) setLoadingPreview(false);
        }
      );
    }

    return () => {
      active = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [file.id, file.type]);

  const currentUserId = (() => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const u = JSON.parse(userStr);
        return u.id ? Number(u.id) : null;
      }
    } catch (e) {
      return null;
    }
    return null;
  })();

  const handleClick = () => {
    if (onOpen) {
      onOpen();
    } else if (onDownload) {
      onDownload();
    }
  };

  const isOwner = file.createdBy !== undefined && currentUserId !== null && Number(file.createdBy) === Number(currentUserId);

  const menuItems = buildMenu(file, onMove, onRename, onShare, onDownload, onOpen, onDelete, isAdmin);
  const menuProps: MenuProps = { items: menuItems, style: DROPDOWN_MENU_STYLE };

  const showMenu = (isAdmin || isOwner) && (menuItems?.length ?? 0) > 0;

  const dragStyle: React.CSSProperties = isDragging ? { opacity: 0.4 } : {};
  const overlayStyle: React.CSSProperties = isDragOverlay
    ? { boxShadow: '0 8px 28px rgba(0,0,0,0.18)', cursor: 'grabbing', opacity: 1 }
    : {};

  if (variant === 'list') {
    return (
      <div
        ref={setNodeRef}
        className={`lib-list-row lib-list-row--file${isDragging ? ' lib-list-row--dragging' : ''}`}
        style={{ ...dragStyle, ...overlayStyle, cursor: 'pointer' }}
        onClick={handleClick}
        {...attributes}
        {...listeners}
      >
        <div className="lib-list-row__icon lib-list-row__icon--file" style={{ background: meta.bg }}>
          <span style={{ color: meta.color }} className="lib-type-chip">{meta.label}</span>
        </div>
        <span className="lib-list-row__name">{file.name}</span>
        <span className="lib-list-row__cell">{file.owner}</span>
        <span className="lib-list-row__cell">{formatFileSize(file.size)}</span>
        <span className="lib-list-row__cell">{file.updatedAt.slice(0, 10)}</span>
        {!isDragOverlay && showMenu && (
          <Dropdown menu={menuProps} trigger={['click']} placement="bottomRight">
            <MoreBtn onClick={(e) => e.stopPropagation()} />
          </Dropdown>
        )}
        {(!showMenu || isDragOverlay) && <span />}
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      className={`lib-file-card${isDragging ? ' lib-file-card--dragging' : ''}`}
      style={{ ...dragStyle, ...overlayStyle, cursor: 'pointer' }}
      onClick={handleClick}
      {...attributes}
      {...listeners}
    >
      <div className="lib-file-card__header">
        <div className="lib-file-card__icon" style={{ background: meta.bg }}>
          <span style={{ color: meta.color }} className="lib-type-chip">{meta.label}</span>
        </div>
        <span className="lib-file-card__name" title={file.name}>{file.name}</span>
        {!isDragOverlay && showMenu && (
          <Dropdown menu={menuProps} trigger={['click']} placement="bottomRight">
            <MoreBtn onClick={(e) => e.stopPropagation()} />
          </Dropdown>
        )}
      </div>
      <div className="lib-file-card__preview" style={{ background: meta.bg, position: 'relative', overflow: 'hidden' }}>
        {(file.type === 'IMAGE' || file.type === 'PDF') && previewUrl ? (
          <img
            src={previewUrl || "/file_placeholder.png"}
            onError={(e) => {
              (e.target as HTMLImageElement).src = "/file_placeholder.png";
            }}
            alt={file.name}
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        ) : (
          <img
            src={"/file_placeholder.png"}
            onError={(e) => {
              (e.target as HTMLImageElement).src = "/file_placeholder.png";
            }}
            alt={file.name}
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        )}
      </div>
    </div>
  );
};

export default FileCard;
